import { Fragment, useCallback } from 'react';
import { Box, Icon, Popover, Tag, Text } from '@nimbus-ds/components';
import { ChevronDownIcon } from '@nimbus-ds/icons';
import { ChargeDto, RecurrencyOptionDto } from '@tiendanube/common';
import { BILLING_NEW_FLOW_FREQUENCY } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { toStatusType } from 'App/rtk';
import { useToastStatus } from 'commons/hooks';
import { capitalizeFirstLetter } from 'commons/utils/capitalizeFirstLetter';
import { combineStatus } from 'commons/utils/combineStatus';
import { useTranslationBilling } from 'domains/Billing';
import { usePatchUpdateSubscriptionRecurrencyMutation } from 'domains/Billing/Checkout/CheckoutApi';
import {
  useFrequencyTranslation,
  useGetSubscriptions,
  useGetTotalForCharges,
  usePaymentMethods,
  useUpdateCharge,
} from 'domains/Billing/Checkout/hooks';
import { useFormatCurrency } from 'domains/Billing/hooks';

interface SelectPlanRecurrencyProps {
  charge?: ChargeDto;
}

export default function SelectPlanRecurrency({
  charge,
}: Readonly<SelectPlanRecurrencyProps>) {
  const t = useTranslationBilling('checkout.selectPlanRecurrency');
  const {
    getChargeDiscountPercentage,
    getTotalForCharges,
    getPriceByMonthWithDiscount,
  } = useGetTotalForCharges();

  const [hasTag] = useHasTags(BILLING_NEW_FLOW_FREQUENCY);
  const { paymentMethods, status: paymentMethodsStatus } =
    usePaymentMethods(hasTag);
  const { status: subscriptionsStatus, getSubscriptionForConcept } =
    useGetSubscriptions(!charge);
  const { isSuccess } = combineStatus(
    paymentMethodsStatus,
    subscriptionsStatus,
  );
  const { getFrequencyDescriptor, getFrequencyInterval } =
    useFrequencyTranslation();
  const formatCurrency = useFormatCurrency();
  const recurrencyOptions = paymentMethods?.[0]?.recurrencyOptions;
  const { resolveVirtualChargeAndUpdate, status: updateChargeStatus } =
    useUpdateCharge(SelectPlanRecurrency.name);
  const [updateSubscription, { status: updateSubscriptionStatus }] =
    usePatchUpdateSubscriptionRecurrencyMutation();
  const { isLoading } = combineStatus(
    updateChargeStatus,
    updateSubscriptionStatus,
  );

  const handleClick = useCallback(
    (option: RecurrencyOptionDto) => {
      const method = paymentMethods?.[0]?.method;
      if (!method || isLoading) return;

      const subscription = getSubscriptionForConcept('plan-cost');
      if (!subscription && !charge) return;

      const data = {
        method,
        recurringFrequency: option.recurringFrequency,
        recurringInterval: option.recurringInterval,
      };

      if (charge) {
        resolveVirtualChargeAndUpdate(charge, data);
      } else {
        if (!subscription) return;
        updateSubscription({
          subscriptionId: subscription.id,
          data,
        });
      }
    },
    [
      resolveVirtualChargeAndUpdate,
      paymentMethods,
      isLoading,
      charge,
      updateSubscription,
      getSubscriptionForConcept,
    ],
  );

  useToastStatus({
    status: charge
      ? updateChargeStatus
      : toStatusType(updateSubscriptionStatus),
    error: t('toast.error'),
    progress: t('toast.loading'),
    success: t('toast.success'),
  });

  if (
    !hasTag ||
    !isSuccess ||
    !recurrencyOptions ||
    recurrencyOptions.length < 2 ||
    recurrencyOptions[0].amountValue === 0
  )
    return null;

  const popoverComponent = (option: RecurrencyOptionDto) => {
    const discountPercentage = getChargeDiscountPercentage(option, true);
    const hasDiscount = discountPercentage > 0;
    const totalWithoutDiscount = getTotalForCharges([option], {
      includeGeneralDiscounts: false,
      includePeriodDiscounts: false,
      includeTaxes: false,
    });
    const totalWithDiscount = getTotalForCharges([option], {
      includeGeneralDiscounts: true,
      includePeriodDiscounts: true,
      includeTaxes: false,
    });
    const priceByMonth = hasDiscount
      ? getPriceByMonthWithDiscount(option)
      : undefined;

    return (
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        onClick={() => handleClick(option)}
        backgroundColor={{
          hover: 'primary-surface',
        }}
        cursor="pointer"
        padding="2"
        borderRadius="2"
      >
        <Box display="flex" flexDirection="column" gap="1">
          <Text>{capitalizeFirstLetter(getFrequencyDescriptor(option))}</Text>
          {!!priceByMonth && (
            <Text color="neutral-textLow" fontSize="caption">{`${formatCurrency(
              priceByMonth,
              option.amountCurrency,
            )} / ${getFrequencyInterval({
              recurringFrequency: 'M',
              recurringInterval: 1,
            })}`}</Text>
          )}
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          gap="1"
          alignItems="flex-end"
        >
          <Box display="inline-flex" flexDirection="row" gap="1">
            <Text textDecoration={hasDiscount ? 'line-through' : 'none'}>
              {formatCurrency(totalWithoutDiscount, option.amountCurrency)}
            </Text>
            {hasDiscount && (
              <Tag appearance="success">{`-${discountPercentage}%`}</Tag>
            )}
          </Box>
          {hasDiscount && (
            <Text>
              {formatCurrency(totalWithDiscount, option.amountCurrency)}
            </Text>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Popover
      padding="none"
      content={
        <Box
          display="flex"
          flexDirection="column"
          gap="2"
          width="100%"
          padding="2"
          paddingY="4"
        >
          {recurrencyOptions.map((option) => (
            <Fragment
              key={`${option.recurringFrequency}-${option.recurringInterval}`}
            >
              {popoverComponent(option)}
            </Fragment>
          ))}
        </Box>
      }
      position="bottom-end"
      enabledClick
      enabledDismiss
      width="20em"
    >
      <Box display="flex" flexDirection="row" gap="1">
        <Text color="primary-interactive">{t('text')}</Text>
        <Icon
          color="primary-interactive"
          source={<ChevronDownIcon size="small" />}
        />
      </Box>
    </Popover>
  );
}
